// Reinsurance Deals list — post-intake view of every ReinsuranceDeal on
// the tenant. From here the user opens a deal to review / edit / add
// market shares. Creation flow is on SubmissionIntelligence (upload →
// AI extract → confirm → POST /reinsurance/deals).

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const S = {
  page:      { flex: 1, overflowY: 'auto', padding: '28px 40px', minHeight: 0 },
  h1:        { fontSize: 24, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' },
  sub:       { color: '#64748b', margin: '0 0 24px' },
  btn:       { padding: '9px 16px', borderRadius: 10, border: 'none', background: '#800020', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  ghost:     { padding: '9px 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  toolbar:   { display: 'flex', gap: 12, marginBottom: 18, alignItems: 'center' },
  table:     { width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', fontSize: 13 },
  th:        { textAlign: 'left', padding: '12px 14px', background: '#f8fafc', color: '#475569', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0' },
  td:        { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  pill:      (status) => ({
    display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
    background: status === 'bound' ? '#dcfce7' : status === 'closed' ? '#e2e8f0' : status === 'cancelled' ? '#fee2e2' : '#fef3c7',
    color: status === 'bound' ? '#166534' : status === 'closed' ? '#475569' : status === 'cancelled' ? '#991b1b' : '#92400e',
  }),
  empty:     { padding: '48px 24px', textAlign: 'center', color: '#64748b' },
};

const fmtMoney = (n, ccy) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return `${ccy || ''} ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`.trim(); }
  catch (e) { return String(n); }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toISOString().slice(0, 10); }
  catch (e) { return String(d).slice(0, 10); }
};

const DealsList = () => {
  const history = useHistory();
  const [deals, setDeals]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const fetchDeals = useCallback(async () => {
    setLoad(true);
    setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/deals?limit=100`, { withCredentials: true });
      const list = resp?.data?.data || [];
      setDeals(list);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load deals');
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  return (
    <AppLayout>
      <div style={S.page}>
        <h1 style={S.h1}>Reinsurance Deals</h1>
        <p style={S.sub}>Structured deals created from submissions. Open a deal to review markets, shares, and commissions.</p>

        <div style={S.toolbar}>
          <button style={S.btn} onClick={() => history.push('/reinsurance/submissions')}>+ New Submission</button>
          <button style={S.ghost} onClick={fetchDeals} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          {error && <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span>}
        </div>

        {(!loading && deals.length === 0) ? (
          <div style={{ ...S.table, padding: 0 }}>
            <div style={S.empty}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#334155' }}>No reinsurance deals yet</div>
              <div style={{ marginTop: 6 }}>Upload a submission to create your first deal.</div>
            </div>
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Deal #</th>
                <th style={S.th}>Cedant</th>
                <th style={S.th}>Line / Subclass</th>
                <th style={S.th}>Structure</th>
                <th style={S.th}>Sum Insured</th>
                <th style={S.th}>Premium</th>
                <th style={S.th}>Period</th>
                <th style={S.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr
                  key={d._id}
                  onClick={() => history.push(`/reinsurance/deals/${d._id}`)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  style={{ cursor: 'pointer', transition: 'background 0.12s' }}
                >
                  <td style={{ ...S.td, fontWeight: 600, color: '#800020' }}>{d.dealNumber || '—'}</td>
                  <td style={S.td}>{d.cedantName || '—'}</td>
                  <td style={S.td}>{[d.lineOfBusiness, d.subclass].filter(Boolean).join(' / ') || '—'}</td>
                  <td style={S.td}>{d.structureType || '—'}</td>
                  <td style={S.td}>{fmtMoney(d.sumInsured, d.currency)}</td>
                  <td style={S.td}>{fmtMoney(d.premium, d.currency)}</td>
                  <td style={S.td}>{fmtDate(d.periodStart)} → {fmtDate(d.periodEnd)}</td>
                  <td style={S.td}><span style={S.pill(d.status)}>{d.status || 'draft'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
};

export default DealsList;
